// User-agent parsing helpers. Two concerns live here:
//   1. Bot detection (used by SSR pages to decide whether to render rich
//      UI vs. crawler-friendly content).
//   2. Coarse browser/OS detection for choosing copy in error states
//      (e.g. the camera/microphone permission help panel).
//
// Order of checks in the browser/OS detection matters because many UAs are
// layered — Chrome on iOS reports both "Safari" and "CriOS", Edge reports
// both "Chrome" and "Edg/", and iPadOS Safari reports "Macintosh" + a
// touch-capable platform. Always evaluate the most-specific signal first.

export const isBot = (userAgent?: string): boolean => {
  if (!userAgent) return false

  const botPatterns = [
    'bot',
    'spider',
    'crawler',
    'googlebot',
    'bingbot',
    'slurp',
    'duckduckbot',
    'baiduspider',
    'yandexbot',
    'facebookexternalhit',
    'twitterbot',
    'rogerbot',
    'linkedinbot',
    'embedly',
    'quora link preview',
    'showyoubot',
    'outbrain',
    'pinterest',
    'slackbot',
    'vkShare',
    'W3C_Validator',
  ]

  const lowerUserAgent = userAgent.toLowerCase()
  return botPatterns.some(bot => lowerUserAgent.includes(bot.toLowerCase()))
}

export type BrowserName = 'chrome' | 'firefox' | 'safari' | 'edge' | 'other'
export type OsName = 'macos' | 'ios' | 'android' | 'windows' | 'linux' | 'other'

export interface BrowserOs {
  browser: BrowserName
  os: OsName
  isMobile: boolean
  // True for iPad / iPadOS. iPadOS Safari (since iOS 13) defaults to a
  // "Desktop" UA that reads as Macintosh, so this field — populated using
  // navigator.maxTouchPoints — is the only reliable iPad signal at the
  // call site. Surfaced as a structured field so consumers don't have to
  // re-derive it from the raw UA.
  isIpad: boolean
}

const UNKNOWN: BrowserOs = {
  browser: 'other',
  os: 'other',
  isMobile: false,
  isIpad: false,
}

export function detectBrowserOs(
  ua?: string,
  // Passed in because navigator.maxTouchPoints is the only reliable way to
  // distinguish iPadOS Safari (which UA-spoofs as macOS) from real macOS.
  maxTouchPoints?: number
): BrowserOs {
  if (typeof ua !== 'string' || ua.length === 0) return UNKNOWN

  const macintoshTouch =
    /macintosh/i.test(ua) &&
    typeof maxTouchPoints === 'number' &&
    maxTouchPoints > 1
  const isIpad = /ipad/i.test(ua) || macintoshTouch

  let os: OsName = 'other'
  if (/iphone|ipod/i.test(ua)) os = 'ios'
  else if (isIpad) os = 'ios'
  else if (/android/i.test(ua)) os = 'android'
  else if (/mac os x|macintosh/i.test(ua)) os = 'macos'
  else if (/windows nt/i.test(ua)) os = 'windows'
  else if (/cros|linux|x11/i.test(ua)) os = 'linux'

  let browser: BrowserName = 'other'
  // Edg/ for Chromium Edge, EdgiOS for iOS Edge, EdgA for Android Edge.
  if (/edg(a|ios)?\//i.test(ua)) browser = 'edge'
  else if (/firefox|fxios/i.test(ua)) browser = 'firefox'
  // CriOS is Chrome on iOS; Chromium covers Brave/Arc/Opera/Vivaldi which
  // all share Chrome's permission UI.
  else if (/crios|chrome|chromium/i.test(ua)) browser = 'chrome'
  else if (/safari/i.test(ua)) browser = 'safari'

  const isMobile = os === 'ios' || os === 'android'
  return { browser, os, isMobile, isIpad }
}

// Convenience for the common client-side case — pulls from `navigator` if
// available, otherwise returns the unknown fallback.
export function detectFromNavigator(): BrowserOs {
  if (typeof navigator === 'undefined') return UNKNOWN
  return detectBrowserOs(navigator.userAgent, navigator.maxTouchPoints)
}

// Whether this browser reliably fires `PermissionStatus.change` for camera
// and microphone when the user toggles a permission in browser settings.
// Used to decide whether to show a manual "Try again" button vs. trust the
// hook's auto-recovery. Every iOS browser is WKWebView underneath (Apple's
// platform rules), so they all inherit Safari's long-standing bug where
// the event doesn't fire — users must manually re-trigger getUserMedia.
// Firefox has a similar gap on its lock-icon "×" clear flow (state goes
// to 'prompt', not 'granted'), so it's bucketed as unreliable too.
export function hasReliablePermissionChangeEvents(info: BrowserOs): boolean {
  if (info.os === 'ios') return false
  if (info.browser === 'safari') return false
  if (info.browser === 'chrome' || info.browser === 'edge') return true
  return false
}
